// Supabase client — replaces lib/firebase.ts.
//
// Two things worth knowing about how this differs from the Firebase setup:
//
//   1. Authorization comes from a `user_role` claim inside the JWT, stamped by
//      the custom access token hook. That means a role change only lands when
//      the token is refreshed. `refreshRole()` below forces it, so an owner
//      promoting a cashier doesn't have to tell them to log out and back in.
//
//   2. There is no service-account equivalent on the client. Anything needing
//      elevated rights goes through a SECURITY DEFINER function or an edge
//      function — never through a privileged key shipped to the browser.

import { createClient, type Session } from '@supabase/supabase-js';
import type { Database } from '@/types/database';

const url = import.meta.env.VITE_SUPABASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

/**
 * True when the app has enough config to talk to Supabase. The router uses
 * this to send a misconfigured deployment to /config-required instead of
 * failing with an opaque network error on every screen.
 */
export const isSupabaseConfigured = Boolean(url && anonKey);

if (!isSupabaseConfigured) {
  // eslint-disable-next-line no-console
  console.error(
    '[supabase] VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY are missing — see .env.example',
  );
}

export const supabase = createClient<Database>(
  url ?? 'http://localhost:54321',
  anonKey ?? 'missing-anon-key',
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: false,
    },
  },
);

export type AppRole = 'owner' | 'cashier';

/** Read the role out of the access token rather than querying `profiles`. */
export function roleFromSession(session: Session | null): AppRole | null {
  if (!session?.access_token) return null;
  try {
    const [, payload] = session.access_token.split('.');
    const claims = JSON.parse(
      atob(payload.replace(/-/g, '+').replace(/_/g, '/')),
    ) as { user_role?: string };
    return claims.user_role === 'owner' || claims.user_role === 'cashier'
      ? claims.user_role
      : null;
  } catch {
    return null;
  }
}

/**
 * Force a token refresh so a just-changed role takes effect immediately.
 * Returns the new role, or null if the account has been deactivated — the
 * hook omits the claim entirely for inactive users.
 */
export async function refreshRole(): Promise<AppRole | null> {
  const { data } = await supabase.auth.refreshSession();
  return roleFromSession(data.session);
}
